/**
 * DriversTab — admin Fleet → Drivers sub-tab.
 *
 * Extracted from FleetPanel.jsx (Phase 2 P2 decomposition · 2026-06).
 * Behaviour byte-identical to the inlined version. All extracted
 * children (DriverFormModal, ConfirmModal, SuspendModal,
 * DriverDiagnoseButton) live in sibling files in this directory.
 *
 * The runMutation helper keeps a per-row busy map so each action
 * spinner is independent. Mutations are awaited and onRefresh is
 * called AFTER server confirms so the rendered state always matches
 * the truth — no flicker, no stale fields.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Trash2, Power, Edit, UserPlus, AlertCircle, Loader2, Ban, RotateCcw, Building2, Clock,
} from "lucide-react";
import { api, formatApiError } from "../../../lib/api";
import { toast } from "sonner";
import { DriverFormModal } from "./DriverFormModal";
import { ConfirmModal } from "./ConfirmModal";
import { SuspendModal } from "./SuspendModal";
import { DriverDiagnoseButton } from "./DriverDiagnoseButton";

const FOUNDING_OP = "crystal-dispatch";

export const DriversTab = ({ drivers, rides, onRefresh }) => {
  const [formOpen, setFormOpen] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [confirm, setConfirm] = useState(null); // { title, message, onConfirm, tone, busyKey }
  const [busyRow, setBusyRow] = useState({}); // { [driverId+":"+action]: true }
  // Operator SaaS — tenant badge. Best-effort operator id→name map; only the
  // platform owner can read this endpoint, so non-owners silently fall back to
  // the founding-tenant label (their own fleet). Purely informational.
  const [opNames, setOpNames] = useState({});
  useEffect(() => {
    let alive = true;
    api.get("/admin/platform/operators")
      .then((r) => {
        if (!alive) return;
        const m = {};
        (r.data?.operators || r.data || []).forEach((o) => {
          if (o.id) m[o.id] = o.name || o.slug || o.id;
        });
        setOpNames(m);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const tenantLabel = (opId) =>
    opNames[opId] || (opId && opId !== FOUNDING_OP ? opId : "Crystal Dispatch");
  // Phase 1 Slice 3 — suspension modal target. When non-null, the
  // SuspendModal is rendered with a reason input so the operator must
  // type a justification before the suspend call fires. Reinstate runs
  // through the existing ConfirmModal because no reason is collected.
  const [suspendingDriver, setSuspendingDriver] = useState(null);

  // Active-trip detection — server enforces the same rule on DELETE, but we
  // surface it visually so admins know why a row can't be deleted.
  const activeDriverIds = useMemo(() => {
    const s = new Set();
    (rides || []).forEach((r) => {
      if (r.driver_id && ["accepted", "in_progress"].includes(r.status)) s.add(r.driver_id);
    });
    return s;
  }, [rides]);

  const setRowBusy = (id, action, val) =>
    setBusyRow((b) => ({ ...b, [`${id}:${action}`]: val }));

  const runMutation = async ({ id, action, fn, successMsg, optimistic, rollback }) => {
    setRowBusy(id, action, true);
    try {
      if (optimistic) optimistic();
      await fn();
      if (successMsg) toast.success(successMsg);
      // refresh AFTER server confirms — replaces optimistic state with truth
      await onRefresh?.();
    } catch (e) {
      if (rollback) rollback();
      toast.error(formatApiError(e));
    } finally {
      setRowBusy(id, action, false);
    }
  };

  const handleToggleOnline = (driver) => {
    const goingOnline = !driver.is_online;
    if (!goingOnline && activeDriverIds.has(driver.id)) {
      setConfirm({
        title: "Force this driver offline?",
        message: `${driver.name} is on an active trip. Forcing them offline only removes them from dispatch — the active ride stays attached. Continue?`,
        tone: "warn",
        confirmLabel: "Force offline",
        onConfirm: () => doToggleOnline(driver, false),
      });
      return;
    }
    doToggleOnline(driver, goingOnline);
  };

  const doToggleOnline = (driver, goingOnline) =>
    runMutation({
      id: driver.id,
      action: "toggle",
      fn: () => api.post(`/admin/drivers/${driver.id}/${goingOnline ? "online" : "offline"}`),
      successMsg: `${driver.name} is now ${goingOnline ? "online" : "offline"}`,
    });

  // Phase 1 Slice 3 — suspension flow. The reason is collected via the
  // SuspendModal (separate from ConfirmModal because that one doesn't
  // accept structured input). Reinstatement is gated through the
  // existing ConfirmModal — no reason required.
  const handleSuspendSubmit = (driver, reason) =>
    runMutation({
      id: driver.id,
      action: "suspend",
      fn: () => api.post(`/admin/drivers/${driver.id}/suspend`, { reason, notify: true }),
      successMsg: `${driver.name} suspended — they've been emailed.`,
    });

  const handleReinstate = (driver) => {
    setConfirm({
      title: "Reinstate this driver?",
      message: `Lift the suspension on ${driver.name} (${driver.email})? They'll be emailed and able to flip themselves back online.`,
      tone: "warn",
      confirmLabel: "Reinstate",
      onConfirm: () =>
        runMutation({
          id: driver.id,
          action: "reinstate",
          fn: () => api.post(`/admin/drivers/${driver.id}/reinstate`, { notify: true }),
          successMsg: `${driver.name} reinstated`,
        }),
    });
  };

  const handleDelete = (driver) => {
    if (activeDriverIds.has(driver.id)) {
      toast.error("Cannot delete — driver is on an active trip. Force-release first.");
      return;
    }
    setConfirm({
      title: "Delete this driver?",
      message: `Permanently delete ${driver.name} (${driver.email})? This cannot be undone. Their historic rides are preserved.`,
      tone: "danger",
      confirmLabel: "Delete driver",
      onConfirm: () =>
        runMutation({
          id: driver.id,
          action: "delete",
          fn: () => api.delete(`/admin/drivers/${driver.id}`),
          successMsg: `${driver.name} deleted`,
        }),
    });
  };

  const handleSaved = (newDriverPassword, isCreate) => {
    setFormOpen(false);
    setEditingDriver(null);
    if (isCreate && newDriverPassword) {
      // One-time password reveal — keep visible until admin dismisses it.
      toast.success("Driver created", {
        description: `Initial password: ${newDriverPassword}`,
        duration: 60_000,
        action: {
          label: "Copy",
          onClick: () => navigator.clipboard?.writeText(newDriverPassword),
        },
      });
    } else {
      toast.success(isCreate ? "Driver created" : "Driver updated");
    }
    onRefresh?.();
  };

  return (
    <div data-testid="drivers-tab">
      <div className="flex items-center justify-between mb-3">
        <div className="label-mini text-muted-foreground">
          {drivers.length} driver{drivers.length === 1 ? "" : "s"}
        </div>
        <button
          onClick={() => { setEditingDriver(null); setFormOpen(true); }}
          className="text-xs px-3 py-2 bg-[#0A0A0A] text-white hover:bg-black font-semibold transition-colors flex items-center gap-1.5"
          data-testid="add-driver-btn"
        >
          <UserPlus className="w-3.5 h-3.5" /> Add driver
        </button>
      </div>

      {drivers.length === 0 ? (
        <div className="text-center py-12 text-sm text-muted-foreground" data-testid="drivers-empty">
          No drivers yet. Add one to start dispatching.
        </div>
      ) : (
        <div className="space-y-1.5">
          {drivers.map((d) => {
            const onTrip = activeDriverIds.has(d.id);
            const toggling = busyRow[`${d.id}:toggle`];
            const deleting = busyRow[`${d.id}:delete`];
            const suspending = busyRow[`${d.id}:suspend`];
            const reinstating = busyRow[`${d.id}:reinstate`];
            const isSuspended = !!d.suspended;
            return (
              <div
                key={d.id}
                className="border border-border p-3 flex items-center justify-between gap-2 transition-card"
                data-testid={`driver-row-${d.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate flex items-center gap-2 flex-wrap">
                    {d.name}
                    {onTrip && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-[#FEF3C7] text-[#92400E] border border-[#F59E0B]"
                        data-testid={`driver-on-trip-${d.id}`}
                        title="Driver is on an active ride"
                      >
                        <AlertCircle className="w-2.5 h-2.5" /> ON TRIP
                      </span>
                    )}
                    {isSuspended && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-[#FEF2F2] text-[#7F1D1D] border border-[#EF4444]"
                        data-testid={`driver-suspended-pill-${d.id}`}
                        title={d.suspension_reason || "Suspended"}
                      >
                        <Ban className="w-2.5 h-2.5" /> SUSPENDED
                      </span>
                    )}
                    {d.compliance_hold && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-[#FFF7ED] text-[#9A3412] border border-[#F97316] font-bold"
                        data-testid={`driver-compliance-hold-${d.id}`}
                        title={d.compliance_hold.problems.map((p) => `${p.doc_label}: ${p.issue.replace("not_approved:", "")}`).join(" · ")}
                      >
                        <AlertCircle className="w-2.5 h-2.5" /> {d.compliance_hold.enforced ? "COMPLIANCE HOLD" : "WOULD BE HELD"}
                      </span>
                    )}
                    <span
                      className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-[#EEF2FF] text-[#3730A3] border border-[#C7D2FE]"
                      data-testid={`driver-tenant-${d.id}`}
                      title="Operator tenant this driver belongs to"
                    >
                      <Building2 className="w-2.5 h-2.5" /> {tenantLabel(d.operator_id)}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border ${d.is_online ? "bg-[#ECFDF5] text-[#065F46] border-[#10B981]" : "bg-secondary text-muted-foreground border-border"}`}
                      data-testid={`driver-online-pill-${d.id}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${d.is_online ? "bg-[#10B981] animate-pulse" : "bg-muted-foreground"}`} />
                      {d.is_online ? "ONLINE" : "OFFLINE"}
                    </span>
                  </div>
                  <div className="label-mini text-muted-foreground truncate font-mono-tab">
                    {d.email}
                    {d.vehicle_plate ? ` · ${d.vehicle_plate}` : ""}
                    {d.vehicle_make ? ` · ${d.vehicle_make}` : ""}
                    {d.phone ? ` · ${d.phone}` : ""}
                  </div>
                  {d.compliance_hold && (
                    <div className="text-[11px] text-[#9A3412] mt-1 truncate" data-testid={`driver-compliance-hold-reason-${d.id}`}>
                      Documents: {d.compliance_hold.problems.map((p) => `${p.doc_label} (${p.issue.replace("not_approved:", "").replace(/_/g, " ")})`).join(", ")}
                    </div>
                  )}
                  {isSuspended && d.suspension_reason && (
                    <div
                      className="text-[11px] text-[#7F1D1D] mt-1 truncate"
                      data-testid={`driver-suspension-reason-${d.id}`}
                      title={d.suspension_reason}
                    >
                      Reason: {d.suspension_reason}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleToggleOnline(d)}
                    disabled={toggling || isSuspended}
                    className={`text-xs px-2 py-1.5 border transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed ${d.is_online ? "border-[#10B981] text-[#065F46] hover:bg-[#ECFDF5]" : "border-border text-muted-foreground hover:bg-secondary"}`}
                    data-testid={`driver-toggle-online-${d.id}`}
                    title={isSuspended ? "Reinstate first to flip online" : (d.is_online ? "Force offline" : "Force online")}
                  >
                    {toggling ? <Loader2 className="w-3 h-3 animate-spin" /> : <Power className="w-3 h-3" />}
                  </button>

                  {isSuspended ? (
                    <button
                      onClick={() => handleReinstate(d)}
                      disabled={reinstating}
                      className="text-xs px-2 py-1.5 border border-[#10B981] text-[#065F46] hover:bg-[#ECFDF5] transition-colors flex items-center gap-1 disabled:opacity-50"
                      data-testid={`driver-reinstate-${d.id}`}
                      title="Reinstate driver"
                    >
                      {reinstating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                    </button>
                  ) : (
                    <button
                      onClick={() => setSuspendingDriver(d)}
                      disabled={suspending}
                      className="text-xs px-2 py-1.5 border border-[#EF4444] text-[#7F1D1D] hover:bg-[#FEF2F2] transition-colors flex items-center gap-1 disabled:opacity-50"
                      data-testid={`driver-suspend-${d.id}`}
                      title="Suspend driver"
                    >
                      {suspending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ban className="w-3 h-3" />}
                    </button>
                  )}

                  <button
                    onClick={() => { setEditingDriver(d); setFormOpen(true); }}
                    className="text-muted-foreground hover:text-[#002FA7] p-2"
                    data-testid={`driver-edit-${d.id}`}
                    title="Edit driver"
                  >
                    <Edit className="w-4 h-4" />
                  </button>

                  <DriverDiagnoseButton driverId={d.id} driverName={d.name} />

                  <button
                    onClick={() => handleDelete(d)}
                    disabled={deleting}
                    className={`p-2 disabled:opacity-40 disabled:cursor-not-allowed ${onTrip ? "text-muted-foreground/60 hover:text-[#F59E0B] cursor-help" : "text-muted-foreground hover:text-destructive"}`}
                    data-testid={`driver-delete-${d.id}`}
                    title={onTrip ? "Driver on active trip — force-release first" : "Delete driver"}
                  >
                    {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {formOpen && (
        <DriverFormModal
          driver={editingDriver}
          onClose={() => { setFormOpen(false); setEditingDriver(null); }}
          onSaved={handleSaved}
        />
      )}

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          tone={confirm.tone}
          confirmLabel={confirm.confirmLabel}
          onCancel={() => setConfirm(null)}
          onConfirm={async () => {
            const fn = confirm.onConfirm;
            setConfirm(null);
            await fn?.();
          }}
        />
      )}

      {suspendingDriver && (
        <SuspendModal
          driver={suspendingDriver}
          onCancel={() => setSuspendingDriver(null)}
          onConfirm={async (reason) => {
            const target = suspendingDriver;
            setSuspendingDriver(null);
            await handleSuspendSubmit(target, reason);
          }}
        />
      )}
    </div>
  );
};

export default DriversTab;
